import React from 'react';
import { Typography, Card, Space, Divider, Collapse, Button } from 'antd';
import { ArrowLeft, FileText, Edit, Save, Search, Tag, Bot, Settings } from 'lucide-react';
import PageContainer from './PageContainer';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface HelpPageProps {
  onBack?: () => void;
}

const HelpPage: React.FC<HelpPageProps> = ({ onBack }) => {
  return (
    <PageContainer>
      {/* 头部 */}
      <div style={{ marginBottom: 32 }}>
        {onBack && (
          <Button
            icon={<ArrowLeft size={16} />}
            onClick={onBack}
            style={{ marginBottom: 16 }}
          >
            返回
          </Button>
        )}
        <Title level={2}>
          <FileText size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          小酷笔记 - 使用帮助
        </Title>
        <Text type="secondary">
          欢迎使用小酷笔记！这里是详细的使用指南，帮助您快速上手。
        </Text>
      </div>

      {/* 快速开始 */}
      <Card title="🚀 快速开始" style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={4}>1. 创建您的第一篇笔记</Title>
            <Paragraph>
              点击右上角的 <Text code>新建文档</Text> 按钮，开始创建您的第一篇笔记。
              支持 Markdown 语法，让您的笔记更加丰富多彩。
            </Paragraph>
          </div>

          <div>
            <Title level={4}>2. 使用标签管理</Title>
            <Paragraph>
              为笔记添加标签，方便分类和查找。在编辑器中可以选择或创建新标签。
            </Paragraph>
          </div>

          <div>
            <Title level={4}>3. AI 智能助手</Title>
            <Paragraph>
              使用内置的 AI 助手来帮助您整理笔记、生成摘要或回答问题。
            </Paragraph>
          </div>
        </Space>
      </Card>

      {/* 详细功能说明 */}
      <Card title="📖 功能详解" style={{ marginBottom: 24 }}>
        <Collapse>
          <Panel header={
            <span>
              <Edit size={16} style={{ marginRight: 8 }} />
              编辑器使用
            </span>
          } key="1">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Paragraph>
                <Text strong>Markdown 支持：</Text>
                <br />
                • <Text code># 标题</Text> - 创建标题
                <br />
                • <Text code>**粗体**</Text> - 粗体文字
                <br />
                • <Text code>*斜体*</Text> - 斜体文字
                <br />
                • <Text code>[链接](URL)</Text> - 创建链接
                <br />
                • <Text code>![图片](URL)</Text> - 插入图片
              </Paragraph>

              <Paragraph>
                <Text strong>快捷键：</Text>
                <br />
                • <Text code>Ctrl + S</Text> - 保存笔记
                <br />
                • <Text code>Ctrl + P</Text> - 切换预览模式
                <br />
                • <Text code>Ctrl + /</Text> - 显示/隐藏帮助
              </Paragraph>
            </Space>
          </Panel>

          <Panel header={
            <span>
              <Tag size={16} style={{ marginRight: 8 }} />
              标签管理
            </span>
          } key="2">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Paragraph>
                <Text strong>创建标签：</Text>
                在编辑器中选择标签时，可以输入新标签名称来创建。
              </Paragraph>

              <Paragraph>
                <Text strong>标签颜色：</Text>
                每个标签都有独特的颜色，帮助您快速识别不同类别的笔记。
              </Paragraph>

              <Paragraph>
                <Text strong>标签筛选：</Text>
                在首页可以按标签筛选笔记，快速找到相关内容。
              </Paragraph>
            </Space>
          </Panel>

          <Panel header={
            <span>
              <Search size={16} style={{ marginRight: 8 }} />
              搜索功能
            </span>
          } key="3">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Paragraph>
                <Text strong>全文搜索：</Text>
                在搜索框中输入关键词，可以搜索笔记标题和内容。
              </Paragraph>

              <Paragraph>
                <Text strong>搜索技巧：</Text>
                <br />
                • 支持中英文搜索
                <br />
                • 不区分大小写
                <br />
                • 支持部分匹配
              </Paragraph>
            </Space>
          </Panel>

          <Panel header={
            <span>
              <Bot size={16} style={{ marginRight: 8 }} />
              AI 助手
            </span>
          } key="4">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Paragraph>
                <Text strong>智能对话：</Text>
                AI 助手可以帮助您：
                <br />
                • 整理和总结笔记内容
                <br />
                • 回答关于笔记的问题
                <br />
                • 提供写作建议和灵感
                <br />
                • 格式化和美化文本
              </Paragraph>

              <Paragraph>
                <Text strong>使用限制：</Text>
                非 VIP 用户每日可使用 10 次 AI 功能。
              </Paragraph>
            </Space>
          </Panel>

          <Panel header={
            <span>
              <Settings size={16} style={{ marginRight: 8 }} />
              设置选项
            </span>
          } key="5">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Paragraph>
                <Text strong>主题设置：</Text>
                支持亮色和暗色主题，可在右上角切换。
              </Paragraph>

              <Paragraph>
                <Text strong>编辑器设置：</Text>
                可以调整字体大小、行高等编辑器参数。
              </Paragraph>

              <Paragraph>
                <Text strong>数据导出：</Text>
                支持导出笔记为 Markdown 格式。
              </Paragraph>
            </Space>
          </Panel>
        </Collapse>
      </Card>

      {/* 常见问题 */}
      <Card title="❓ 常见问题" style={{ marginBottom: 24 }}>
        <Collapse>
          <Panel header="如何同步数据？" key="sync">
            <Paragraph>
              网页版数据自动保存到云端，您可以在任何设备上访问。
              建议下载桌面版获得更好的离线体验。
            </Paragraph>
          </Panel>

          <Panel header="忘记密码怎么办？" key="password">
            <Paragraph>
              在登录页面点击"忘记密码"，输入注册邮箱，
              系统会发送验证码到您的邮箱，按提示重置密码即可。
            </Paragraph>
          </Panel>

          <Panel header="如何升级 VIP？" key="vip">
            <Paragraph>
              点击个人中心，选择合适的 VIP 套餐。
              VIP 用户享有无限 AI 对话、无限笔记存储等特权。
            </Paragraph>
          </Panel>

          <Panel header="数据安全吗？" key="security">
            <Paragraph>
              我们采用企业级加密技术保护您的数据安全，
              定期备份，确保数据不丢失。详见隐私政策。
            </Paragraph>
          </Panel>
        </Collapse>
      </Card>

      {/* 联系我们 */}
      <Card title="📞 联系我们">
        <Space direction="vertical" size="small">
          <Text>如果您在使用过程中遇到问题，欢迎联系我们：</Text>
          <Text>• 邮箱：daily@name666.top</Text>
          <Text>• QQ：3106628438</Text>
          <Text>• 微信：longdz6299</Text>
          <Divider />
          <Text type="secondary">
            我们会在 24 小时内回复您的问题。
          </Text>
        </Space>
      </Card>
    </PageContainer>
  );
};

export default HelpPage;
